import type { Mark } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'

interface MarkWithClass {
  mark: Mark
  from: number
  to: number
}

/**
 * Checks if a specific class is applied to the text within the current selection of the editor.
 *
 * This function examines the current selection in the editor state and determines if the specified
 * class name is applied to the text within that selection. It handles both empty selections (cursor position)
 * and non-empty selections (text ranges).
 *
 * @param editorState - The current state of the editor.
 * @param className - The class name to check for.
 * @returns A boolean indicating whether the specified class is applied to the selected text.
 *
 * For an empty selection (cursor position):
 * - It checks the stored marks or the marks at the cursor position.
 *
 * For a non-empty selection:
 * - It calculates the total length of the selection.
 * - It finds all marks with the specified class within the selection.
 * - It compares the length of text with matching marks to the total selection length.
 * - It also considers marks that exclude the text style mark type.
 *
 * The function returns true if the specified class is applied to all of the selected text,
 * and false otherwise.
 */
export const hasTextStyle = (
  editorState: EditorState,
  className: string,
): boolean => {
  const { empty, ranges } = editorState.selection
  const textStyleMarkType = editorState.schema.marks.textStyle

  if (empty) {
    const marks = editorState.storedMarks || editorState.selection.$from.marks()

    return marks.some(
      (mark) =>
        mark.type === textStyleMarkType && mark.attrs?.class === className,
    )
  }

  let totalLength = 0
  const marksWithClass: MarkWithClass[] = []

  ranges.forEach(({ $from, $to }) => {
    const fromPos = $from.pos
    const toPos = $to.pos

    editorState.doc.nodesBetween(fromPos, toPos, (node, pos) => {
      if (!node.isText && !node.marks.length) return

      const start = Math.max(fromPos, pos)
      const end = Math.min(toPos, pos + node.nodeSize)

      totalLength += end - start

      node.marks.forEach((mark) => {
        marksWithClass.push({ mark, from: start, to: end })
      })
    })
  })

  if (totalLength === 0) return false

  const matchingMarksLength = marksWithClass
    .filter(({ mark }) => !textStyleMarkType || mark.type === textStyleMarkType)
    .filter(({ mark }) => mark.attrs?.class.includes(className))
    .reduce((sum, { from, to }) => sum + to - from, 0)

  const nonMatchingMarksLength = marksWithClass
    .filter(
      ({ mark }) =>
        !textStyleMarkType ||
        (mark.type !== textStyleMarkType &&
          mark.type.excludes(textStyleMarkType)),
    )
    .reduce((sum, { from, to }) => sum + to - from, 0)

  return (
    (matchingMarksLength > 0
      ? matchingMarksLength + nonMatchingMarksLength
      : matchingMarksLength) >= totalLength
  )
}

export const removeClass = (
  classString: string,
  ...classNamePatterns: Array<string>
) => {
  classNamePatterns.forEach((pattern) => {
    const regex = new RegExp(`\\b${pattern}[^\\s]*\\b`, 'g')

    classString = classString.replace(regex, '').trim()
  })

  return classString
}

export const removeHTMLSymbols = (message: string) => {
  return message.replace(/<[^>]*>?|\s+/gm, '')
}
